import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { PriorityLevel, TicketCategory, TicketStatus } from '@sib/api';
import { TicketFileDto } from '@sib/api';
import { useTicketsAppTranslation, useTranslation } from '@sib/i18n';
import { FileActionsOption, FileWithMetadata } from '@sib/ui';
import { MediumPriority, HighPriority, LowPriority } from '@sib/ui/icons';
import { z } from 'zod';

export const createFilesFieldSchema = () =>
  z.object({
    files: z.array(z.instanceof(File)),
  });

export const existingFileSchema = z.object({
  id: z.string(),
  showInDocumentsRepo: z.boolean(),
  desiredShowInDocumentsRepo: z.boolean(),
  markedForDelete: z.boolean(),
});

export type ExistingFileFormValue = z.infer<typeof existingFileSchema>;

export const existingFilesFieldSchema = z.record(
  z.string(),
  existingFileSchema
);

const areRecordsEqual = <T,>(
  left: Record<string, T>,
  right: Record<string, T>,
  isEqual: (a: T, b: T) => boolean
): boolean => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) return false;

  return leftKeys.every(key => {
    if (!(key in right)) return false;

    return isEqual(left[key], right[key]);
  });
};

const areExistingFilesEqual = (
  a: ExistingFilesRecord,
  b: ExistingFilesRecord
) =>
  areRecordsEqual(
    a,
    b,
    (l, r) =>
      l.showInDocumentsRepo === r.showInDocumentsRepo &&
      l.desiredShowInDocumentsRepo === r.desiredShowInDocumentsRepo &&
      l.markedForDelete === r.markedForDelete
  );

export const useSyncExistingFilesToForm = (
  documents: TicketFileDto[] | undefined
) => {
  const { getValues, setValue, formState } =
    useFormContext<Record<string, ExistingFilesRecord | undefined>>();

  useEffect(() => {
    if (!documents) return;

    const current = (getValues(TicketFormFilesField.ExistingFiles) ??
      {}) as ExistingFilesRecord;
    const next: ExistingFilesRecord = {};

    for (const doc of documents) {
      const existing = current[doc.id];

      next[doc.id] = existing
        ? { ...existing, showInDocumentsRepo: doc.showInDocumentsRepo }
        : {
            id: doc.id,
            showInDocumentsRepo: doc.showInDocumentsRepo,
            desiredShowInDocumentsRepo: doc.showInDocumentsRepo,
            markedForDelete: false,
          };
    }

    if (areExistingFilesEqual(current, next)) return;

    setValue(TicketFormFilesField.ExistingFiles, next as ExistingFilesRecord, {
      shouldDirty: formState.isDirty,
      shouldValidate: false,
    });
  }, [documents, getValues, setValue, formState]);
};

export const useSelectSharedProps = (isLoading: boolean) =>
  useMemo(
    () => ({
      selectTriggerProps: {
        className: 'h-10',
      },
      disabled: isLoading,
    }),
    [isLoading]
  );

export const useTicketTypeOptionsWithText = (
  ticketTypeOptions: Array<{ label: string; value: string }>
) =>
  useMemo(
    () =>
      ticketTypeOptions.map(option => ({
        text: option.label,
        value: option.value,
      })),
    [ticketTypeOptions]
  );

export const useTicketFormFileActionsOptions = (): FileActionsOption[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        label: t('components.fileUpload.fileActions.addToDocumentRepository'),
        isDocsRepoAction: true,
      },
      {
        label: t('components.fileUpload.fileActions.deleteDocument'),
        labelProps: {
          className: 'text-danger-strong',
        },
        isDeleteAction: true,
      },
    ],
    [t]
  );
};
export const processTicketFilesForSubmit = (files: FileWithMetadata[]) => {
  const showInDocumentsRepo = files.map(
    file => file?.shouldAddToDocsRepo ?? false
  );

  return {
    files,
    showInDocumentsRepo,
  };
};

export type ExistingFilesRecord = Record<string, ExistingFileFormValue>;

export enum TicketFormFilesField {
  Files = 'files',
  ExistingFiles = 'existingFiles',
}

export const useExistingFileActions = (fileId: string) => {
  const { watch, setValue, getValues } = useFormContext<{
    [key: string]: ExistingFilesRecord;
  }>();

  const file = watch(TicketFormFilesField.ExistingFiles)?.[fileId];

  const toggleDocsRepo = useCallback(() => {
    const files = getValues(TicketFormFilesField.ExistingFiles) ?? {};
    const current = files[fileId];
    if (!current || current.markedForDelete) return;

    setValue(
      TicketFormFilesField.ExistingFiles,
      {
        ...files,
        [fileId]: {
          ...current,
          desiredShowInDocumentsRepo: !current.desiredShowInDocumentsRepo,
        },
      },
      { shouldDirty: true }
    );
  }, [fileId, getValues, setValue]);

  const toggleDelete = useCallback(() => {
    const files = getValues(TicketFormFilesField.ExistingFiles) ?? {};
    const current = files[fileId];
    if (!current) return;

    setValue(
      TicketFormFilesField.ExistingFiles,
      {
        ...files,
        [fileId]: {
          ...current,
          markedForDelete: !current.markedForDelete,
        },
      },
      { shouldDirty: true }
    );
  }, [fileId, getValues, setValue]);

  return {
    file,
    toggleDocsRepo,
    toggleDelete,
  };
};

export const markFileToDelete = (
  getValues: () => ExistingFilesRecord,
  setValue: (
    value: ExistingFilesRecord,
    options?: { shouldDirty?: boolean }
  ) => void,
  fileId: string,
  marked: boolean
) => {
  const files = getValues() ?? {};
  const current = files[fileId];
  if (!current || current.markedForDelete === marked) return;

  setValue(
    {
      ...files,
      [fileId]: { ...current, markedForDelete: marked },
    },
    { shouldDirty: true }
  );
};

export const useTicketPriorityLabel = () => {
  const { t } = useTicketsAppTranslation();

  return useMemo(
    () => ({
      [PriorityLevel.Low]: t('enums.ticketPriority.low'),
      [PriorityLevel.Medium]: t('enums.ticketPriority.medium'),
      [PriorityLevel.High]: t('enums.ticketPriority.high'),
    }),
    [t]
  );
};

export const useTicketStatusLabel = () => {
  const { t } = useTicketsAppTranslation();

  return useMemo(
    () => ({
      [TicketStatus.New]: t('enums.ticketStatus.new'),
      [TicketStatus.InProgress]: t('enums.ticketStatus.inProgress'),
      [TicketStatus.Closed]: t('enums.ticketStatus.closed'),
      [TicketStatus.WorkingWithVendor]: t(
        'enums.ticketStatus.workingWithVendor'
      ),
      [TicketStatus.AwaitingClientAction]: t(
        'enums.ticketStatus.awaitingClientAction'
      ),
      [TicketStatus.AwaitingInternalAction]: t(
        'enums.ticketStatus.awaitingInternalAction'
      ),
      [TicketStatus.Canceled]: t('enums.ticketStatus.canceled'),
      [TicketStatus.Completed]: t('enums.ticketStatus.completed'),
    }),
    [t]
  );
};

export const useTicketStatusOptions = () => {
  const { t } = useTicketsAppTranslation();

  return useMemo(
    () => [
      {
        value: TicketStatus.New,
        label: t('enums.ticketStatus.new'),
        color: ticketStatusColors[TicketStatus.New],
      },
      {
        value: TicketStatus.InProgress,
        label: t('enums.ticketStatus.inProgress'),
        color: ticketStatusColors[TicketStatus.InProgress],
      },
      {
        value: TicketStatus.Closed,
        label: t('enums.ticketStatus.closed'),
        color: ticketStatusColors[TicketStatus.Closed],
      },
      {
        value: TicketStatus.WorkingWithVendor,
        label: t('enums.ticketStatus.workingWithVendor'),
        color: ticketStatusColors[TicketStatus.WorkingWithVendor],
      },
      {
        value: TicketStatus.AwaitingClientAction,
        label: t('enums.ticketStatus.awaitingClientAction'),
        color: ticketStatusColors[TicketStatus.AwaitingClientAction],
      },
      {
        value: TicketStatus.AwaitingInternalAction,
        label: t('enums.ticketStatus.awaitingInternalAction'),
        color: ticketStatusColors[TicketStatus.AwaitingInternalAction],
      },
      {
        value: TicketStatus.Canceled,
        label: t('enums.ticketStatus.canceled'),
        color: ticketStatusColors[TicketStatus.Canceled],
      },
      {
        value: TicketStatus.Completed,
        label: t('enums.ticketStatus.completed'),
        color: ticketStatusColors[TicketStatus.Completed],
      },
    ],
    [t]
  );
};

export const useTicketTypeOptions = () => {
  const { t } = useTicketsAppTranslation();

  return useMemo(
    () => [
      {
        value: TicketCategory.Account,
        label: t('enums.ticketType.account'),
      },
      {
        value: TicketCategory.Baselines,
        label: t('enums.ticketType.baselines'),
      },
      {
        value: TicketCategory.Client,
        label: t('enums.ticketType.client'),
      },
      {
        value: TicketCategory.Contract,
        label: t('enums.ticketType.contract'),
      },
      {
        value: TicketCategory.CustomerService,
        label: t('enums.ticketType.customerService'),
      },
      {
        value: TicketCategory.Escalation,
        label: t('enums.ticketType.escalation'),
      },
      {
        value: TicketCategory.Exceptions,
        label: t('enums.ticketType.exceptions'),
      },
      {
        value: TicketCategory.Finance,
        label: t('enums.ticketType.finance'),
      },
      {
        value: TicketCategory.FollowUp,
        label: t('enums.ticketType.followUp'),
      },
      {
        value: TicketCategory.InformationRequest,
        label: t('enums.ticketType.informationRequest'),
      },
      {
        value: TicketCategory.Invoice,
        label: t('enums.ticketType.invoice'),
      },
      {
        value: TicketCategory.MasterData,
        label: t('enums.ticketType.masterData'),
      },
      {
        value: TicketCategory.Negotiation,
        label: t('enums.ticketType.negotiation'),
      },
      {
        value: TicketCategory.Others,
        label: t('enums.ticketType.others'),
      },
      {
        value: TicketCategory.Payments,
        label: t('enums.ticketType.payments'),
      },
      {
        value: TicketCategory.Property,
        label: t('enums.ticketType.property'),
      },
      {
        value: TicketCategory.ServiceRequest,
        label: t('enums.ticketType.serviceRequest'),
      },
      {
        value: TicketCategory.Transitions,
        label: t('enums.ticketType.transitions'),
      },
      {
        value: TicketCategory.VendorLocal,
        label: t('enums.ticketType.vendorLocal'),
      },
      {
        value: TicketCategory.MissingBill,
        label: t('enums.ticketType.missingBill'),
      },
      {
        value: TicketCategory.UpdateMailingAddress,
        label: t('enums.ticketType.updateMailingAddress'),
      },
      {
        value: TicketCategory.PotentialLeak,
        label: t('enums.ticketType.potentialLeak'),
      },
      {
        value: TicketCategory.InvoiceQuestion,
        label: t('enums.ticketType.invoiceQuestion'),
      },
    ],
    [t]
  );
};

export const useTicketPriorityOptions = () => {
  const { t } = useTicketsAppTranslation();

  return useMemo(
    () => [
      {
        value: PriorityLevel.Low,
        label: t('enums.ticketPriority.low'),
        Icon: (
          <LowPriority
            style={{ color: ticketPriorityIconColors[PriorityLevel.Low] }}
          />
        ),
      },
      {
        value: PriorityLevel.Medium,
        label: t('enums.ticketPriority.medium'),
        Icon: (
          <MediumPriority
            style={{ color: ticketPriorityIconColors[PriorityLevel.Medium] }}
          />
        ),
      },
      {
        value: PriorityLevel.High,
        label: t('enums.ticketPriority.high'),
        Icon: (
          <HighPriority
            style={{ color: ticketPriorityIconColors[PriorityLevel.High] }}
          />
        ),
      },
    ],
    [t]
  );
};

export const ticketPriorityIconColors: Record<PriorityLevel, string> = {
  [PriorityLevel.Low]: 'hsl(var(--info))',
  [PriorityLevel.Medium]: 'hsl(var(--warning))',
  [PriorityLevel.High]: 'hsl(var(--accent-pink))',
};

export const ticketPriorityColors: Record<PriorityLevel, string> = {
  [PriorityLevel.Low]: 'hsl(var(--info-subtle))',
  [PriorityLevel.Medium]: 'hsl(var(--warning-subtle))',
  [PriorityLevel.High]: 'hsl(var(--accent-pink-soft))',
};

export const ticketStatusColors: Record<TicketStatus, string> = {
  [TicketStatus.New]: 'hsl(var(--success-subtle))',
  [TicketStatus.InProgress]: 'hsl(var(--warning-subtle-alt))',
  [TicketStatus.Closed]: 'hsl(var(--neutral-soft))',
  [TicketStatus.WorkingWithVendor]: 'hsl(var(--info-soft))',
  [TicketStatus.AwaitingClientAction]: 'hsl(var(--info-soft))',
  [TicketStatus.AwaitingInternalAction]: 'hsl(var(--info-soft))',
  [TicketStatus.Canceled]: 'hsl(var(--danger-subtle))',
  [TicketStatus.Completed]: 'hsl(var(--success-subtle))',
};
